import type {
  AdminCreditAdjustmentInput,
  AdminTierUpdateInput,
  CreateTestAccountInput,
} from "@/features/admin/admin.types";

export type CreateTestAccountField =
  | "email"
  | "firstName"
  | "lastName"
  | "password";

export type CreditAdjustmentField = "amount" | "reason";
export type BanReasonField = "reason";
export type TierUpdateField = "reason" | "tier";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isBlank(value: string) {
  return value.trim().length === 0;
}

export function validateCreateTestAccountInput(input: CreateTestAccountInput) {
  const errors: Partial<Record<CreateTestAccountField, string>> = {};

  if (!emailPattern.test(input.email.trim().toLowerCase())) {
    errors.email = "adminWorkspace.validation.email";
  }

  if (isBlank(input.firstName)) {
    errors.firstName = "adminWorkspace.validation.firstNameRequired";
  }

  if (isBlank(input.lastName)) {
    errors.lastName = "adminWorkspace.validation.lastNameRequired";
  }

  if (input.password.length < 8) {
    errors.password = "adminWorkspace.validation.passwordLength";
  }

  return errors;
}

export function validateCreditAdjustmentInput(
  input: Omit<AdminCreditAdjustmentInput, "userId">,
) {
  const errors: Partial<Record<CreditAdjustmentField, string>> = {};

  if (!Number.isInteger(input.amount) || input.amount < 1) {
    errors.amount = "adminWorkspace.validation.amountPositive";
  }

  if (isBlank(input.reason)) {
    errors.reason = "adminWorkspace.validation.reasonRequired";
  }

  return errors;
}

export function validateBanReason(reason: string) {
  return isBlank(reason) ? "adminWorkspace.validation.reasonRequired" : null;
}

export function validateTierUpdateInput(
  input: Omit<AdminTierUpdateInput, "userId">,
  currentTier: AdminTierUpdateInput["tier"],
) {
  const errors: Partial<Record<TierUpdateField, string>> = {};

  if (input.tier !== "free" && input.tier !== "crafter" && input.tier !== "author") {
    errors.tier = "adminWorkspace.validation.tierRequired";
  } else if (input.tier === currentTier) {
    errors.tier = "adminWorkspace.validation.tierDifferent";
  }

  if (isBlank(input.reason)) {
    errors.reason = "adminWorkspace.validation.reasonRequired";
  }

  return errors;
}

export function applyCreditDeductionFloor(currentBalance: number, requestedAmount: number) {
  const normalizedBalance = Number.isFinite(currentBalance)
    ? Math.max(0, Math.trunc(currentBalance))
    : 0;
  const normalizedAmount = Number.isFinite(requestedAmount)
    ? Math.max(0, Math.trunc(requestedAmount))
    : 0;

  return Math.max(0, normalizedBalance - normalizedAmount);
}
